'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';

// ─── Types ────────────────────────────────────────────────────────────────────
type KycStatus = 'not_submitted' | 'pending' | 'approved' | 'rejected';
type WithdrawalStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';

interface KycRecord {
  id: string;
  full_legal_name: string;
  date_of_birth: string | null;
  nationality: string | null;
  id_type: string;
  id_number: string | null;
  address_line1: string | null;
  address_city: string | null;
  address_country: string | null;
  kyc_status: KycStatus;
  rejection_reason: string | null;
  submitted_at: string | null;
}

interface BankAccount {
  id: string;
  account_holder_name: string;
  bank_name: string;
  account_number: string;
  routing_number: string | null;
  swift_code: string | null;
  iban: string | null;
  account_type: string;
  currency: string;
  country: string;
  is_default: boolean;
  is_verified: boolean;
  created_at: string;
}

interface WithdrawalRequest {
  id: string;
  bank_account_id: string | null;
  amount_usd: number;
  fee_usd: number;
  net_amount_usd: number;
  withdrawal_status: WithdrawalStatus;
  reference_number: string | null;
  notes: string | null;
  requested_at: string;
  processed_at: string | null;
}

interface WalletData {
  balance_usd: number;
  total_earned_usd: number;
  pending_usd: number;
}

// ─── Status config ─────────────────────────────────────────────────────────────
const WITHDRAWAL_STATUS: Record<WithdrawalStatus, { label: string; cls: string; icon: string }> = {
  pending:    { label: 'Pending',    cls: 'bg-amber-100 text-amber-700',       icon: 'ClockIcon' },
  processing: { label: 'Processing', cls: 'bg-blue-100 text-blue-700',         icon: 'ArrowPathIcon' },
  completed:  { label: 'Completed',  cls: 'bg-emerald-100 text-emerald-700',   icon: 'CheckCircleIcon' },
  failed:     { label: 'Failed',     cls: 'bg-red-100 text-red-700',           icon: 'XCircleIcon' },
  cancelled:  { label: 'Cancelled',  cls: 'bg-muted text-muted-foreground',    icon: 'MinusCircleIcon' },
};

const KYC_STATUS: Record<KycStatus, { label: string; cls: string; icon: string }> = {
  not_submitted: { label: 'Not Submitted', cls: 'bg-muted text-muted-foreground',     icon: 'ExclamationCircleIcon' },
  pending:       { label: 'Under Review',  cls: 'bg-amber-100 text-amber-700',        icon: 'ClockIcon' },
  approved:      { label: 'Verified',      cls: 'bg-emerald-100 text-emerald-700',    icon: 'ShieldCheckIcon' },
  rejected:      { label: 'Rejected',      cls: 'bg-red-100 text-red-700',            icon: 'ShieldExclamationIcon' },
};

type ActiveTab = 'withdraw' | 'history' | 'bank-accounts' | 'kyc';

export default function ConsultantWithdrawalsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>('withdraw');
  const [loading, setLoading] = useState(true);

  // Data
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [kyc, setKyc] = useState<KycRecord | null>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);

  // Withdraw form
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [selectedBankId, setSelectedBankId] = useState('');
  const [withdrawNotes, setWithdrawNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Bank account form
  const [showBankForm, setShowBankForm] = useState(false);
  const [bankForm, setBankForm] = useState({
    account_holder_name: '', bank_name: '', account_number: '',
    routing_number: '', swift_code: '', iban: '',
    account_type: 'checking', currency: 'USD', country: '',
  });
  const [savingBank, setSavingBank] = useState(false);
  const [bankError, setBankError] = useState('');

  // KYC form
  const [showKycForm, setShowKycForm] = useState(false);
  const [kycForm, setKycForm] = useState({
    full_legal_name: '', date_of_birth: '', nationality: '',
    id_type: 'passport', id_number: '',
    address_line1: '', address_city: '', address_country: '',
  });
  const [savingKyc, setSavingKyc] = useState(false);
  const [kycError, setKycError] = useState('');

  const fetchData = useCallback(async () => {
    if (!user) return;
    const supabase = createClient();
    try {
      const [kycRes, banksRes, withdrawalsRes] = await Promise.all([
        supabase.from('consultant_kyc').select('*').eq('user_id', user.id).maybeSingle(),
        supabase.from('consultant_bank_accounts').select('*').eq('user_id', user.id).order('is_default', { ascending: false }),
        supabase.from('consultant_withdrawal_requests').select('*').eq('user_id', user.id).order('requested_at', { ascending: false }),
      ]);

      setKyc(kycRes.data ?? null);
      setBankAccounts(banksRes.data ?? []);
      setWithdrawals(withdrawalsRes.data ?? []);

      // Auto-select default bank
      const defaultBank = banksRes.data?.find((b: BankAccount) => b.is_default);
      if (defaultBank) setSelectedBankId(defaultBank.id);

      // Fetch wallet balance from consultant_wallets using user id as consultant_id
      const { data: walletData } = await supabase
        .from('consultant_wallets')
        .select('balance_usd, total_earned_usd, pending_usd')
        .eq('consultant_id', user.id)
        .maybeSingle();

      setWallet(walletData ?? { balance_usd: 0, total_earned_usd: 0, pending_usd: 0 });
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Pre-fill KYC form when editing
  useEffect(() => {
    if (kyc) {
      setKycForm({
        full_legal_name: kyc.full_legal_name ?? '',
        date_of_birth: kyc.date_of_birth ?? '',
        nationality: kyc.nationality ?? '',
        id_type: kyc.id_type ?? 'passport',
        id_number: kyc.id_number ?? '',
        address_line1: kyc.address_line1 ?? '',
        address_city: kyc.address_city ?? '',
        address_country: kyc.address_country ?? '',
      });
    }
  }, [kyc]);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleWithdraw = async () => {
    if (!user || !selectedBankId || !withdrawAmount) return;
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) { setSubmitError('Enter a valid amount.'); return; }
    if (amount > (wallet?.balance_usd ?? 0)) { setSubmitError('Amount exceeds available balance.'); return; }

    setSubmitting(true);
    setSubmitError('');
    const supabase = createClient();
    const fee = parseFloat((amount * 0.005).toFixed(2)); // 0.5% fee
    const net = parseFloat((amount - fee).toFixed(2));
    const ref = `WD-${Date.now()}`;

    try {
      const { error } = await supabase.from('consultant_withdrawal_requests').insert({
        user_id: user.id,
        bank_account_id: selectedBankId,
        amount_usd: amount,
        fee_usd: fee,
        net_amount_usd: net,
        withdrawal_status: 'pending',
        reference_number: ref,
        notes: withdrawNotes || null,
      });
      if (error) throw error;
      setSubmitSuccess(true);
      setWithdrawAmount('');
      setWithdrawNotes('');
      await fetchData();
      setTimeout(() => setSubmitSuccess(false), 4000);
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit withdrawal request.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveBank = async () => {
    if (!user) return;
    if (!bankForm.account_holder_name || !bankForm.bank_name || !bankForm.account_number || !bankForm.country) {
      setBankError('Please fill in all required fields.');
      return;
    }
    setSavingBank(true);
    setBankError('');
    const supabase = createClient();
    try {
      const isFirst = bankAccounts.length === 0;
      const { error } = await supabase.from('consultant_bank_accounts').insert({
        user_id: user.id,
        ...bankForm,
        is_default: isFirst,
      });
      if (error) throw error;
      setShowBankForm(false);
      setBankForm({ account_holder_name: '', bank_name: '', account_number: '', routing_number: '', swift_code: '', iban: '', account_type: 'checking', currency: 'USD', country: '' });
      await fetchData();
    } catch (err: unknown) {
      setBankError(err instanceof Error ? err.message : 'Failed to save bank account.');
    } finally {
      setSavingBank(false);
    }
  };

  const handleSetDefaultBank = async (id: string) => {
    if (!user) return;
    const supabase = createClient();
    await supabase.from('consultant_bank_accounts').update({ is_default: false }).eq('user_id', user.id);
    await supabase.from('consultant_bank_accounts').update({ is_default: true }).eq('id', id);
    setSelectedBankId(id);
    await fetchData();
  };

  const handleDeleteBank = async (id: string) => {
    const supabase = createClient();
    await supabase.from('consultant_bank_accounts').delete().eq('id', id);
    await fetchData();
  };

  const handleSubmitKyc = async () => {
    if (!user) return;
    if (!kycForm.full_legal_name || !kycForm.id_type || !kycForm.address_country) {
      setKycError('Please fill in all required fields.');
      return;
    }
    setSavingKyc(true);
    setKycError('');
    const supabase = createClient();
    try {
      const payload = {
        user_id: user.id,
        ...kycForm,
        date_of_birth: kycForm.date_of_birth || null,
        kyc_status: 'pending' as KycStatus,
        submitted_at: new Date().toISOString(),
      };
      if (kyc?.id) {
        const { error } = await supabase.from('consultant_kyc').update(payload).eq('id', kyc.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('consultant_kyc').insert(payload);
        if (error) throw error;
      }
      setShowKycForm(false);
      await fetchData();
    } catch (err: unknown) {
      setKycError(err instanceof Error ? err.message : 'Failed to submit KYC.');
    } finally {
      setSavingKyc(false);
    }
  };

  const fmtCurrency = (v: number) => `$${v.toFixed(2)}`;
  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const maskAccount = (n: string) => `···${n.slice(-4)}`;

  const kycApproved = kyc?.kyc_status === 'approved';
  const hasBanks = bankAccounts.length > 0;
  const canWithdraw = kycApproved && hasBanks && (wallet?.balance_usd ?? 0) > 0;

  const totalWithdrawn = withdrawals.filter(w => w.withdrawal_status === 'completed').reduce((s, w) => s + w.net_amount_usd, 0);
  const pendingWithdrawals = withdrawals.filter(w => ['pending', 'processing'].includes(w.withdrawal_status)).reduce((s, w) => s + w.amount_usd, 0);

  if (loading) {
    return (
      <AppLayout activeRoute="/consultant-withdrawals">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Loading wallet…</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout activeRoute="/consultant-withdrawals">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-700 text-foreground mb-1">Wallet & Withdrawals</h1>
            <p className="text-sm text-muted-foreground">Request payouts to your bank account and track withdrawal history</p>
          </div>
          <Link href="/consultant-dashboard" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-muted text-muted-foreground hover:text-foreground text-sm font-500 transition-colors">
            ← Dashboard
          </Link>
        </div>

        {/* Wallet Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Available Balance', value: fmtCurrency(wallet?.balance_usd ?? 0), icon: 'WalletIcon', cls: 'gradient-teal text-white' },
            { label: 'Total Earned', value: fmtCurrency(wallet?.total_earned_usd ?? 0), icon: 'BanknotesIcon', cls: 'bg-card border border-border' },
            { label: 'Total Withdrawn', value: fmtCurrency(totalWithdrawn), icon: 'ArrowUpCircleIcon', cls: 'bg-card border border-border' },
            { label: 'In Progress', value: fmtCurrency(pendingWithdrawals), icon: 'ClockIcon', cls: 'bg-card border border-border' },
          ].map((card) => (
            <div key={card.label} className={`rounded-2xl p-4 ${card.cls}`}>
              <div className={`flex items-center gap-2 mb-2 ${card.cls.includes('gradient') ? 'text-white/80' : 'text-muted-foreground'}`}>
                <Icon name={card.icon as Parameters<typeof Icon>[0]['name']} size={16} />
                <span className="text-xs font-500">{card.label}</span>
              </div>
              <p className={`text-xl font-700 ${card.cls.includes('gradient') ? 'text-white' : 'text-foreground'}`}>{card.value}</p>
            </div>
          ))}
        </div>

        {/* KYC Status Banner */}
        {!kycApproved && (
          <div className={`rounded-2xl p-4 flex items-start gap-3 ${kyc?.kyc_status === 'pending' ? 'bg-amber-50 border border-amber-200' : 'bg-destructive/5 border border-destructive/20'}`}>
            <Icon
              name={kyc?.kyc_status === 'pending' ? 'ClockIcon' : 'ShieldExclamationIcon'}
              size={20}
              className={kyc?.kyc_status === 'pending' ? 'text-amber-600 mt-0.5' : 'text-destructive mt-0.5'}
            />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-600 ${kyc?.kyc_status === 'pending' ? 'text-amber-800' : 'text-destructive'}`}>
                {kyc?.kyc_status === 'pending' ? 'KYC verification under review' : kyc?.kyc_status === 'rejected' ? 'KYC verification rejected' : 'Identity verification required'}
              </p>
              <p className={`text-xs mt-0.5 ${kyc?.kyc_status === 'pending' ? 'text-amber-700' : 'text-muted-foreground'}`}>
                {kyc?.kyc_status === 'pending' ?'Your documents are being reviewed. Withdrawals will be enabled once approved.'
                  : kyc?.kyc_status === 'rejected'
                  ? `Reason: ${kyc.rejection_reason ?? 'Please resubmit your documents.'}`
                  : 'Complete KYC verification to enable withdrawals to your bank account.'}
              </p>
            </div>
            <button
              onClick={() => { setActiveTab('kyc'); setShowKycForm(true); }}
              className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-600 hover:opacity-90 transition-opacity"
            >
              {kyc ? 'Resubmit' : 'Verify Now'}
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-xl w-fit">
          {([
            { id: 'withdraw', label: 'Withdraw', icon: 'ArrowUpCircleIcon' },
            { id: 'history', label: 'History', icon: 'ClockIcon' },
            { id: 'bank-accounts', label: 'Bank Accounts', icon: 'BuildingLibraryIcon' },
            { id: 'kyc', label: 'KYC', icon: 'ShieldCheckIcon' },
          ] as { id: ActiveTab; label: string; icon: string }[]).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-500 transition-all duration-150 ${
                activeTab === tab.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={15} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ── TAB: Withdraw ─────────────────────────────────────────────────── */}
        {activeTab === 'withdraw' && (
          <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
            <h2 className="text-base font-600 text-foreground">Request Withdrawal</h2>

            {!canWithdraw && (
              <div className="rounded-xl bg-muted p-4 text-sm text-muted-foreground space-y-1">
                {!kycApproved && <p className="flex items-center gap-2"><Icon name="ShieldExclamationIcon" size={15} /> Complete KYC verification first</p>}
                {!hasBanks && <p className="flex items-center gap-2"><Icon name="BuildingLibraryIcon" size={15} /> Add a bank account to receive funds</p>}
                {(wallet?.balance_usd ?? 0) <= 0 && <p className="flex items-center gap-2"><Icon name="WalletIcon" size={15} /> No available balance to withdraw</p>}
              </div>
            )}

            {/* Amount */}
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Amount (USD)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-600">$</span>
                <input
                  type="number"
                  min="10"
                  step="0.01"
                  max={wallet?.balance_usd ?? 0}
                  value={withdrawAmount}
                  onChange={e => setWithdrawAmount(e.target.value)}
                  disabled={!canWithdraw}
                  placeholder="0.00"
                  className="w-full pl-7 pr-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
                />
              </div>
              {withdrawAmount && parseFloat(withdrawAmount) > 0 && (
                <div className="mt-2 p-3 rounded-xl bg-muted text-xs space-y-1">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Withdrawal amount</span><span>${parseFloat(withdrawAmount || '0').toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Processing fee (0.5%)</span><span>-${(parseFloat(withdrawAmount || '0') * 0.005).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-600 text-foreground border-t border-border pt-1">
                    <span>You receive</span><span>${(parseFloat(withdrawAmount || '0') * 0.995).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Bank account selector */}
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Destination Bank Account</label>
              {bankAccounts.length === 0 ? (
                <button
                  onClick={() => { setActiveTab('bank-accounts'); setShowBankForm(true); }}
                  className="w-full py-2.5 px-4 rounded-xl border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors text-left"
                >
                  + Add a bank account
                </button>
              ) : (
                <div className="space-y-2">
                  {bankAccounts.map(acc => (
                    <label key={acc.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selectedBankId === acc.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`}>
                      <input type="radio" name="bank" value={acc.id} checked={selectedBankId === acc.id} onChange={() => setSelectedBankId(acc.id)} className="accent-primary" />
                      <Icon name="BuildingLibraryIcon" size={18} className="text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-500 text-foreground">{acc.bank_name} {maskAccount(acc.account_number)}</p>
                        <p className="text-xs text-muted-foreground">{acc.account_holder_name} · {acc.currency} · {acc.country}</p>
                      </div>
                      {acc.is_verified && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-500">Verified</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Notes (optional)</label>
              <textarea
                rows={2}
                value={withdrawNotes}
                onChange={e => setWithdrawNotes(e.target.value)}
                disabled={!canWithdraw}
                placeholder="Any notes for this withdrawal…"
                className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
              />
            </div>

            {submitSuccess && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm">
                <Icon name="CheckCircleIcon" size={16} />
                Withdrawal request submitted successfully!
              </div>
            )}
            {submitError && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-destructive/5 border border-destructive/20 text-destructive text-sm">
                <Icon name="ExclamationCircleIcon" size={16} />
                {submitError}
              </div>
            )}

            <button
              onClick={handleWithdraw}
              disabled={!canWithdraw || submitting || !withdrawAmount || !selectedBankId}
              className="w-full py-3 rounded-xl gradient-teal text-white font-600 text-sm hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing…</>
              ) : (
                <><Icon name="ArrowUpCircleIcon" size={16} /> Request Withdrawal</>
              )}
            </button>
          </div>
        )}

        {/* ── TAB: History ──────────────────────────────────────────────────── */}
        {activeTab === 'history' && (
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <h2 className="text-base font-600 text-foreground">Payout History</h2>
              <p className="text-xs text-muted-foreground mt-0.5">{withdrawals.length} total requests</p>
            </div>
            {withdrawals.length === 0 ? (
              <div className="py-16 text-center">
                <Icon name="ClockIcon" size={40} className="text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No withdrawal requests yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {withdrawals.map(w => {
                  const cfg = WITHDRAWAL_STATUS[w.withdrawal_status];
                  const bank = bankAccounts.find(b => b.id === w.bank_account_id);
                  return (
                    <div key={w.id} className="px-6 py-4 flex items-center gap-4 hover:bg-muted/30 transition-colors">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.cls}`}>
                        <Icon name={cfg.icon as Parameters<typeof Icon>[0]['name']} size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-600 text-foreground">{fmtCurrency(w.amount_usd)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-500 ${cfg.cls}`}>{cfg.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {bank ? `${bank.bank_name} ${maskAccount(bank.account_number)}` : 'Bank account'}
                          {w.reference_number && ` · ${w.reference_number}`}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-600 text-foreground">{fmtCurrency(w.net_amount_usd)}</p>
                        <p className="text-xs text-muted-foreground">{fmtDate(w.requested_at)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: Bank Accounts ────────────────────────────────────────────── */}
        {activeTab === 'bank-accounts' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-600 text-foreground">Bank Accounts</h2>
              <button
                onClick={() => setShowBankForm(!showBankForm)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-500 hover:opacity-90 transition-opacity"
              >
                <Icon name="PlusIcon" size={15} />
                Add Account
              </button>
            </div>

            {/* Add bank form */}
            {showBankForm && (
              <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
                <h3 className="text-sm font-600 text-foreground">New Bank Account</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { key: 'account_holder_name', label: 'Account Holder Name *', placeholder: 'Full legal name' },
                    { key: 'bank_name', label: 'Bank Name *', placeholder: 'e.g. Chase Bank' },
                    { key: 'account_number', label: 'Account Number *', placeholder: '000000000' },
                    { key: 'routing_number', label: 'Routing Number', placeholder: 'US routing number' },
                    { key: 'swift_code', label: 'SWIFT / BIC Code', placeholder: 'e.g. CHASUS33' },
                    { key: 'iban', label: 'IBAN', placeholder: 'International bank account number' },
                    { key: 'country', label: 'Country *', placeholder: 'e.g. US, AE, GB' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-xs font-500 text-muted-foreground mb-1">{f.label}</label>
                      <input
                        type="text"
                        value={(bankForm as Record<string, string>)[f.key]}
                        onChange={e => setBankForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        className="w-full px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs font-500 text-muted-foreground mb-1">Account Type</label>
                    <select
                      value={bankForm.account_type}
                      onChange={e => setBankForm(prev => ({ ...prev, account_type: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="checking">Checking</option>
                      <option value="savings">Savings</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-500 text-muted-foreground mb-1">Currency</label>
                    <select
                      value={bankForm.currency}
                      onChange={e => setBankForm(prev => ({ ...prev, currency: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      {['USD', 'EUR', 'GBP', 'AED', 'KES', 'PKR', 'IDR', 'MYR'].map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {bankError && <p className="text-xs text-destructive">{bankError}</p>}
                <div className="flex gap-2">
                  <button onClick={handleSaveBank} disabled={savingBank} className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-500 hover:opacity-90 transition-opacity disabled:opacity-50">
                    {savingBank ? 'Saving…' : 'Save Account'}
                  </button>
                  <button onClick={() => setShowBankForm(false)} className="px-4 py-2 rounded-xl bg-muted text-muted-foreground text-sm font-500 hover:text-foreground transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Bank account list */}
            {bankAccounts.length === 0 && !showBankForm ? (
              <div className="bg-card border border-border rounded-2xl py-16 text-center">
                <Icon name="BuildingLibraryIcon" size={40} className="text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No bank accounts added yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {bankAccounts.map(acc => (
                  <div key={acc.id} className={`bg-card border rounded-2xl p-4 flex items-center gap-4 ${acc.is_default ? 'border-primary/40' : 'border-border'}`}>
                    <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                      <Icon name="BuildingLibraryIcon" size={20} className="text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-600 text-foreground">{acc.bank_name}</p>
                        {acc.is_default && <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-500">Default</span>}
                        {acc.is_verified && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-500">Verified</span>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{acc.account_holder_name} · {maskAccount(acc.account_number)} · {acc.currency}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!acc.is_default && (
                        <button onClick={() => handleSetDefaultBank(acc.id)} className="text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-lg hover:bg-muted">
                          Set default
                        </button>
                      )}
                      <button onClick={() => handleDeleteBank(acc.id)} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors">
                        <Icon name="TrashIcon" size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: KYC ──────────────────────────────────────────────────────── */}
        {activeTab === 'kyc' && (
          <div className="space-y-4">
            {/* KYC status card */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${KYC_STATUS[kyc?.kyc_status ?? 'not_submitted'].cls}`}>
                    <Icon name={KYC_STATUS[kyc?.kyc_status ?? 'not_submitted'].icon as Parameters<typeof Icon>[0]['name']} size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-600 text-foreground">Identity Verification (KYC)</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-500 ${KYC_STATUS[kyc?.kyc_status ?? 'not_submitted'].cls}`}>
                      {KYC_STATUS[kyc?.kyc_status ?? 'not_submitted'].label}
                    </span>
                  </div>
                </div>
                {kyc?.kyc_status !== 'pending' && (
                  <button
                    onClick={() => setShowKycForm(!showKycForm)}
                    className="px-3 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-500 hover:opacity-90 transition-opacity"
                  >
                    {kyc ? 'Update KYC' : 'Submit KYC'}
                  </button>
                )}
              </div>

              {kyc && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  {[
                    { label: 'Legal Name', value: kyc.full_legal_name },
                    { label: 'Nationality', value: kyc.nationality ?? '—' },
                    { label: 'ID Type', value: kyc.id_type },
                    { label: 'City', value: kyc.address_city ?? '—' },
                    { label: 'Country', value: kyc.address_country ?? '—' },
                    { label: 'Submitted', value: kyc.submitted_at ? fmtDate(kyc.submitted_at) : '—' },
                  ].map(f => (
                    <div key={f.label}>
                      <p className="text-xs text-muted-foreground">{f.label}</p>
                      <p className="font-500 text-foreground mt-0.5">{f.value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* KYC form */}
            {showKycForm && (
              <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
                <h3 className="text-sm font-600 text-foreground">KYC Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { key: 'full_legal_name', label: 'Full Legal Name *', placeholder: 'As on government ID', type: 'text' },
                    { key: 'date_of_birth', label: 'Date of Birth', placeholder: '', type: 'date' },
                    { key: 'nationality', label: 'Nationality', placeholder: 'e.g. UAE, US, UK', type: 'text' },
                    { key: 'id_number', label: 'ID / Passport Number', placeholder: 'Document number', type: 'text' },
                    { key: 'address_line1', label: 'Address', placeholder: 'Street address', type: 'text' },
                    { key: 'address_city', label: 'City', placeholder: 'City', type: 'text' },
                    { key: 'address_country', label: 'Country *', placeholder: 'e.g. AE, US, GB', type: 'text' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-xs font-500 text-muted-foreground mb-1">{f.label}</label>
                      <input
                        type={f.type}
                        value={(kycForm as Record<string, string>)[f.key]}
                        onChange={e => setKycForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        className="w-full px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs font-500 text-muted-foreground mb-1">ID Type *</label>
                    <select
                      value={kycForm.id_type}
                      onChange={e => setKycForm(prev => ({ ...prev, id_type: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="passport">Passport</option>
                      <option value="national_id">National ID</option>
                      <option value="drivers_license">Driver&apos;s License</option>
                      <option value="residence_permit">Residence Permit</option>
                    </select>
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-muted text-xs text-muted-foreground flex items-start gap-2">
                  <Icon name="InformationCircleIcon" size={14} className="mt-0.5 flex-shrink-0" />
                  <span>Your documents are encrypted and used only for identity verification. We do not share your data with third parties.</span>
                </div>

                {kycError && <p className="text-xs text-destructive">{kycError}</p>}
                <div className="flex gap-2">
                  <button onClick={handleSubmitKyc} disabled={savingKyc} className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-500 hover:opacity-90 transition-opacity disabled:opacity-50">
                    {savingKyc ? 'Submitting…' : 'Submit for Review'}
                  </button>
                  <button onClick={() => setShowKycForm(false)} className="px-4 py-2 rounded-xl bg-muted text-muted-foreground text-sm font-500 hover:text-foreground transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* KYC info */}
            <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
              <h3 className="text-sm font-600 text-foreground">Why KYC is required</h3>
              <div className="space-y-2">
                {[
                  { icon: 'ShieldCheckIcon', text: 'Comply with anti-money laundering (AML) regulations' },
                  { icon: 'LockClosedIcon', text: 'Protect your account from unauthorized withdrawals' },
                  { icon: 'BanknotesIcon', text: 'Enable direct bank transfers to your account' },
                  { icon: 'ClockIcon', text: 'Verification typically takes 1–3 business days' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-primary flex-shrink-0" />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>
    </AppLayout>
  );
}
